/**
 * i18n setup — the portal owns its translations (ADR-0013).
 *
 * We deliberately do NOT use app.newshoes.com's translations-service: it is a
 * different product on a different VPS, and ADR-0004's boundary says the two
 * share HTTP contracts and nothing else. A translation outage there must not be
 * able to blank this UI.
 *
 * Catalogues are lazy-loaded per locale, so a customer downloads exactly one
 * language. This is the first real code-split boundary in the app and it exists
 * to protect ADR-0010's ≤150 KB initial-route budget.
 *
 * ⚠️ Before adding a string, read .claude/skills/i18n-and-locale/SKILL.md —
 *    in particular the rule that every "invalid credentials" surface resolves
 *    to ONE key. Splitting them reintroduces the enumeration oracle ADR-0003
 *    exists to close.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// ⚠️ ORDER IS LOAD-BEARING for detectLocale(): a browser reporting a bare `es`
//    (or es-AR, es-MX…) gets the FIRST supported locale with that language
//    prefix. es-CL stays ahead of es-EC on purpose — Chile was the first
//    Spanish market and the larger one; do not reorder to "tidy up".
export const SUPPORTED_LOCALES = ['pt-BR', 'es-CL', 'es-EC'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** pt-BR is the source language: every key exists here first. */
export const DEFAULT_LOCALE: Locale = 'pt-BR';

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Static import map, NOT a template literal.
 *
 * `import(\`./locales/${l}.json\`)` would make Vite bundle every locale it can
 * glob, which defeats the split. An explicit map gives one chunk per locale and
 * fails at build time if a file is missing.
 */
const CATALOGUES: Record<Locale, () => Promise<{ default: Record<string, unknown> }>> = {
  'pt-BR': () => import('./locales/pt-BR.json'),
  'es-CL': () => import('./locales/es-CL.json'),
  'es-EC': () => import('./locales/es-EC.json'),
};

const loaded = new Set<Locale>();

async function loadCatalogue(locale: Locale): Promise<void> {
  if (loaded.has(locale)) return;
  const mod = await CATALOGUES[locale]();
  i18n.addResourceBundle(locale, 'translation', mod.default, true, true);
  loaded.add(locale);
}

/**
 * Best supported match for what the browser reports.
 *
 * Matches the region-less prefix too, so a `es-AR` or plain `es` browser gets
 * es-CL rather than falling all the way back to Portuguese. That is a better
 * wrong answer than the alternative.
 */
export function detectLocale(): Locale {
  const candidates = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language];

  for (const candidate of candidates) {
    if (isSupportedLocale(candidate)) return candidate;
    const prefix = candidate.split('-')[0]?.toLowerCase();
    const match = SUPPORTED_LOCALES.find((l) => l.split('-')[0]?.toLowerCase() === prefix);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

/** Keep the document in sync so screen readers and hyphenation follow the UI. */
function syncDocumentLang(locale: Locale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export async function initI18n(initial?: Locale): Promise<typeof i18n> {
  const locale = initial ?? detectLocale();

  await i18n.use(initReactI18next).init({
    lng: locale,
    // Every Spanish locale falls back to pt-BR, the source language — a missing Spanish key
    // shows correct Portuguese rather than a raw key.
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    resources: {},
    interpolation: {
      // React already escapes. Double-escaping mangles apostrophes in Spanish.
      escapeValue: false,
    },
    // Loud in dev, silent in production: a missing key must be obvious while
    // building and must never show a raw key to a customer.
    saveMissing: import.meta.env.DEV,
    // `false`, not `undefined` — exactOptionalPropertyTypes rejects an explicit
    // undefined on an optional property.
    missingKeyHandler: import.meta.env.DEV
      ? (_lngs: readonly string[], _ns: string, key: string): void => {
          console.error(`[i18n] missing key: ${key}`);
        }
      : false,
    returnNull: false,
  });

  // The fallback catalogue must be present or a missing key renders empty.
  await loadCatalogue(DEFAULT_LOCALE);
  if (locale !== DEFAULT_LOCALE) await loadCatalogue(locale);

  syncDocumentLang(locale);
  return i18n;
}

export async function setLocale(locale: Locale): Promise<void> {
  await loadCatalogue(locale);
  await i18n.changeLanguage(locale);
  syncDocumentLang(locale);
}

export function currentLocale(): Locale {
  return isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}

export default i18n;
